import { defineStyleConfig, type StyleFunctionProps } from "@chakra-ui/react";

/**
 * Button variants:
 *   primary      — pill, leaf.500 fill, navy.900 text (never white on green), green glow + 1px lift on hover
 *   secondary    — white, 1px sand ring → navy ring on hover (DEFAULT variant)
 *   soft         — navy.50 fill, navy.700 text
 *   ghost        — transparent, sand.100 on hover
 *   danger       — red.500 fill, white text
 *   danger-soft  — red.50 fill, red.800 text (Logout)
 *   link         — underlined text.link
 *   nav          — sidebar pill (pair with motion.div layoutId="nav-pill" and aria-current="page")
 *   segment      — segmented-control item (pair with aria-pressed)
 *
 * Legacy bridge (until the Appendix-A sweep removes every colorScheme="blue"/"green"/"red" button without a variant):
 *   a Button with NO variant and colorScheme="blue" | "green" renders as `primary`, colorScheme="red" renders as `danger-soft`.
 *   Explicit `variant="secondary"` (default colorScheme "navy") is never affected.
 */
const primary = {
  borderRadius: "full",
  bg: "leaf.500",
  color: "navy.900",
  _hover: {
    bg: "leaf.400",
    boxShadow: "glowGreen",
    transform: "translateY(-1px)",
    // Variant _hover is emitted after base _focusVisible; nest the ring so hover + keyboard focus still shows it.
    _focusVisible: { boxShadow: "outline" },
    _disabled: { bg: "leaf.500", boxShadow: "none", transform: "none", _focusVisible: { boxShadow: "outline" } },
  },
  _active: { bg: "leaf.600", transform: "scale(0.98)" },
};

const secondary = {
  bg: "surface.card",
  color: "text.primary",
  boxShadow: "0 0 0 1px var(--chakra-colors-border-strong)",
  _hover: {
    boxShadow: "0 0 0 1px var(--chakra-colors-navy-900)",
    bg: "sand.50",
    _focusVisible: { boxShadow: "outline" },
    _disabled: {
      boxShadow: "0 0 0 1px var(--chakra-colors-border-strong)",
      bg: "surface.card",
      _focusVisible: { boxShadow: "outline" },
    },
  },
  _active: { bg: "sand.100" },
  // baseStyle's _disabled clears boxShadow; keep the hairline so a disabled secondary button still reads as a button.
  _disabled: {
    boxShadow: "0 0 0 1px var(--chakra-colors-border-strong)",
    bg: "surface.card",
    _focusVisible: { boxShadow: "outline" },
  },
};

const dangerSoft = {
  bg: "red.50",
  color: "red.800",
  _hover: { bg: "red.100", _disabled: { bg: "red.50" } },
  _active: { bg: "red.200" },
};

export const Button = defineStyleConfig({
  baseStyle: {
    fontFamily: "body",
    fontWeight: 700,
    borderRadius: "md",
    letterSpacing: "0.01em",
    transitionProperty: "common",
    transitionDuration: "fast",
    _focusVisible: { boxShadow: "outline" },
    _active: { transform: "scale(0.98)" },
    // A button disabled via aria-disabled (e.g. Pagination) can keep keyboard focus: nest the ring so it outranks
    // the disabled box-shadow, which is emitted after _focusVisible with the same specificity.
    _disabled: {
      opacity: 0.5,
      cursor: "not-allowed",
      boxShadow: "none",
      transform: "none",
      _focusVisible: { boxShadow: "outline" },
    },
  },
  sizes: {
    xs: { h: "1.875rem", minW: "1.875rem", px: 3, fontSize: "0.75rem" },
    sm: { h: "2.25rem", minW: "2.25rem", px: 3.5, fontSize: "0.8125rem" },
    md: { h: "2.75rem", minW: "2.75rem", px: 5, fontSize: "0.875rem" },
    lg: { h: "3.25rem", minW: "3.25rem", px: 7, fontSize: "1rem" },
  },
  variants: {
    primary,
    secondary: (props: StyleFunctionProps) => {
      if (props.colorScheme === "blue" || props.colorScheme === "green") return primary;
      if (props.colorScheme === "red") return dangerSoft;
      return secondary;
    },
    soft: {
      bg: "navy.50",
      color: "navy.700",
      _hover: { bg: "navy.100", _disabled: { bg: "navy.50" } },
      _active: { bg: "navy.200" },
    },
    ghost: {
      bg: "transparent",
      color: "text.secondary",
      _hover: { bg: "sand.100", color: "text.primary", _disabled: { bg: "transparent" } },
      _active: { bg: "sand.200" },
    },
    danger: {
      bg: "red.500",
      color: "white",
      _hover: { bg: "red.600", _disabled: { bg: "red.500" } },
      _active: { bg: "red.800" },
    },
    "danger-soft": dangerSoft,
    link: {
      color: "text.link",
      p: 0,
      h: "auto",
      minW: 0,
      fontWeight: 700,
      textDecoration: "underline",
      textUnderlineOffset: "3px",
      _hover: { color: "navy.600" },
      _active: { color: "navy.700", transform: "none" },
    },
    nav: {
      w: "full",
      h: "3rem",
      px: 4,
      justifyContent: "flex-start",
      gap: 3,
      borderRadius: "full",
      fontWeight: 600,
      fontSize: "0.9375rem",
      color: "navy.200",
      position: "relative",
      bg: "transparent",
      _hover: { bg: "rgba(255,255,255,0.08)", color: "white" },
      _active: { transform: "none" },
      _focusVisible: { boxShadow: "outlineOnDark" },
      "&[aria-current=page]": { color: "navy.900", fontWeight: 800, _hover: { bg: "transparent" } },
    },
    segment: {
      h: "2.25rem",
      px: 3.5,
      fontSize: "0.8125rem",
      borderRadius: "sm",
      bg: "transparent",
      color: "text.secondary",
      position: "relative",
      _hover: { color: "text.primary" },
      _active: { transform: "none" },
      // Ring nested here so it outranks the pressed shadow (both would otherwise tie on specificity).
      "&[aria-pressed=true]": {
        color: "text.primary",
        bg: "surface.card",
        boxShadow: "xs",
        _focusVisible: { boxShadow: "outline" },
      },
    },
  },
  defaultProps: { variant: "secondary", size: "md" },
});
